import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Star, Car, MapPin, Globe, Award, Loader2, AlertCircle, MessageSquare, Users, Briefcase, Calendar, Palette, Hash, ChevronLeft, ChevronRight, Phone, Mail } from 'lucide-react';
import { api } from '../services/api';

const CAT_LABELS = { punctuality: 'Pünktlichkeit', cleanliness: 'Sauberkeit', friendliness: 'Freundlichkeit', driving_style: 'Fahrstil' };
const TYPE_LABELS = { economy: 'Economy', comfort: 'Comfort', business: 'Business', premium: 'Premium', vip: 'VIP', suv: 'SUV', van: 'Van', minibus: 'Kleinbus', bus: 'Bus' };

function Stars({ value, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} size={size} className={s <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'} />
      ))}
    </div>
  );
}

function VehicleCard({ vehicle, isOnly }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid={`vehicle-card-${vehicle.id}`}>
      {/* Vehicle Photo */}
      {vehicle.photo && !imgError ? (
        <div className="w-full h-48 sm:h-56 bg-slate-100 overflow-hidden">
          <img
            src={vehicle.photo}
            alt={`${vehicle.make} ${vehicle.model}`}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        </div>
      ) : (
        <div className="w-full h-32 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
          <Car size={48} className="text-slate-200" />
        </div>
      )}

      {/* Vehicle Info */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-slate-800">{vehicle.make} {vehicle.model}</h3>
          <span className="px-2.5 py-1 bg-orange-50 text-orange-600 rounded-lg text-xs font-semibold capitalize">
            {TYPE_LABELS[vehicle.vehicle_type] || vehicle.vehicle_type}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {vehicle.year && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Calendar size={14} className="text-slate-400 shrink-0" />
              <span>{vehicle.year}</span>
            </div>
          )}
          {vehicle.color && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Palette size={14} className="text-slate-400 shrink-0" />
              <span>{vehicle.color}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Users size={14} className="text-slate-400 shrink-0" />
            <span>{vehicle.passengers || '–'} Passagiere</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Briefcase size={14} className="text-slate-400 shrink-0" />
            <span>{vehicle.luggage || '–'} Gepäck</span>
          </div>
          {vehicle.plate_number && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Hash size={14} className="text-slate-400 shrink-0" />
              <span>{vehicle.plate_number}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DriverPublicProfile() {
  const { driverId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/driver/${driverId}`)
      .then(res => setProfile(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Fahrer nicht gefunden'))
      .finally(() => setLoading(false));
  }, [driverId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 size={36} className="text-orange-500 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="text-center bg-white rounded-2xl p-8 max-w-md w-full border border-slate-100">
        <AlertCircle size={40} className="mx-auto text-red-400 mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Fehler</h2>
        <p className="text-slate-500 mb-4">{error}</p>
        <a href="/" className="text-orange-500 hover:underline text-sm">Zur Startseite</a>
      </div>
    </div>
  );

  const vehicles = profile.vehicles || [];
  const firstVehicle = vehicles[0] || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="driver-public-profile">
      <nav className="bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-baseline gap-0.5">
            <span className="text-xl font-extrabold text-orange-500">366</span>
            <span className="text-xs font-semibold text-slate-800">Transfer</span>
          </a>
          <span className="text-sm text-slate-500">Fahrerprofil</span>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-start gap-5">
            <div className="w-20 h-20 rounded-2xl bg-orange-500 flex items-center justify-center text-white text-3xl font-bold shrink-0">
              {profile.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-slate-800" data-testid="profile-name">{profile.name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <div className="flex items-center gap-1">
                  <Stars value={profile.rating} size={16} />
                  <span className="text-sm font-semibold text-slate-800 ml-1">{profile.rating}</span>
                  <span className="text-xs text-slate-400">({profile.ratings_count} Bewertungen)</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.region && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">
                    <MapPin size={12} /> {profile.region}
                  </span>
                )}
                {profile.languages?.length > 0 && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">
                    <Globe size={12} /> {profile.languages.join(', ')}
                  </span>
                )}
                <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">
                  <Award size={12} /> {profile.total_trips} Fahrten
                </span>
                {vehicles.length > 0 && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-orange-50 rounded-lg text-xs text-orange-600">
                    <Car size={12} /> {vehicles.length} {vehicles.length === 1 ? 'Fahrzeug' : 'Fahrzeuge'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Contact info */}
          {(profile.phone || profile.email) && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-4">
              {profile.phone && (
                <a href={`tel:${profile.phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-orange-500 transition-colors" data-testid="driver-phone">
                  <Phone size={14} className="text-slate-400" /> {profile.phone}
                </a>
              )}
              {profile.email && (
                <a href={`mailto:${profile.email}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-orange-500 transition-colors" data-testid="driver-email">
                  <Mail size={14} className="text-slate-400" /> {profile.email}
                </a>
              )}
            </div>
          )}
        </div>

        {/* Vehicles Section */}
        {vehicles.length > 0 && (
          <div>
            <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Car size={18} className="text-orange-500" /> Fahrzeuge ({vehicles.length})
            </h2>
            <div className="space-y-4">
              {vehicles.map(v => (
                <VehicleCard key={v.id} vehicle={v} isOnly={vehicles.length === 1} />
              ))}
            </div>
          </div>
        )}

        {/* Rating Categories */}
        {profile.ratings_count > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-bold text-slate-800 mb-4">Bewertungen</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(CAT_LABELS).map(([key, label]) => (
                <div key={key} className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-600">{label}</span>
                    <span className="text-sm font-bold text-slate-800">{profile.avg_ratings?.[key] || 0}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-amber-400 h-2 rounded-full" style={{ width: `${((profile.avg_ratings?.[key] || 0) / 5) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <MessageSquare size={18} className="text-orange-500" /> Kundenbewertungen ({profile.ratings_count})
          </h2>
          {profile.ratings?.length === 0 ? (
            <p className="text-slate-400 text-center py-8">Noch keine Bewertungen</p>
          ) : (
            <div className="space-y-4">
              {profile.ratings?.map(r => (
                <div key={r.id} className="border-b border-slate-100 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                        {r.customer_name?.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-slate-700">{r.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Stars value={r.average} size={12} />
                      <span className="text-xs font-semibold text-slate-600 ml-1">{r.average}</span>
                    </div>
                  </div>
                  {r.comment && <p className="text-sm text-slate-600 ml-10">{r.comment}</p>}
                  <p className="text-xs text-slate-400 ml-10 mt-1">{new Date(r.created_at).toLocaleDateString('de-DE')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
